import type { AssessmentStatus } from '@claritio/frontend/types'
import i18n from '@claritio/frontend/lib/i18n'

// Single source of truth for how an assessment status is presented across the
// app (Home portfolio, Assessments list, Assessment detail). Keeps the label
// and colour consistent instead of each page re-deriving them. Labels are
// localized via i18n (ENG-111); colour/progress stay language-agnostic.

type BadgeVariant = 'success' | 'muted' | 'violet' | 'info' | 'secondary'

// ENG-134: status pills are decorative, not functional signals — they go neutral
// (the label carries the meaning). Progress fills go mono. Genuine alert colour
// (SLA risk, destructive) stays and lives elsewhere (AssessmentShow/Home SLA chips).
const STATUS: Record<AssessmentStatus, { variant: BadgeVariant; progress: string }> = {
  active:    { variant: 'secondary', progress: 'bg-primary' },
  draft:     { variant: 'muted',     progress: 'bg-muted-foreground/40' },
  completed: { variant: 'secondary', progress: 'bg-primary' },
  published: { variant: 'secondary', progress: 'bg-primary' },
  closed:    { variant: 'muted',     progress: 'bg-muted-foreground/40' },
}

export const assessmentStatusLabel = (s: AssessmentStatus) => i18n.t(`common:status.${s}`)
export const assessmentStatusVariant = (s: AssessmentStatus): BadgeVariant => STATUS[s].variant
export const assessmentProgressClass = (s: AssessmentStatus) => STATUS[s].progress

// The status bar (AssessmentStatusBar) is the one place the consultant reads
// draft vs live; a page that changes the status tells it to reload.
const STATUS_CHANGED = 'claritio:assessment-status-changed'
export const announceAssessmentStatusChange = () => window.dispatchEvent(new Event(STATUS_CHANGED))
export function onAssessmentStatusChange(cb: () => void) {
  window.addEventListener(STATUS_CHANGED, cb)
  return () => window.removeEventListener(STATUS_CHANGED, cb)
}
